import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { ChevronIcon, Link, LinkVariant, Slider, SlidingDirections } from '@jsmdg/yoshi';
import { type Product } from '../../../shared/types';
import { type TrackingCategory, type TrackingListName } from '../../enums';
import { trackSlide } from '../../helper/trackSlide';
import { useHighestMatchingBreakpoint } from '../../hooks';
import { productSliderMessages as messages } from '../../i18n';
import { ProductTileWrapper, type ProductTileWrapperComponent } from '../ProductTileWrapper';
import { SkeletonTile } from '../SkeletonTile/SkeletonTile';
import { productSliderConfig } from './productSliderConfig';
import styles from './ProductSlider.module.scss';

const messagesSlider = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const ProductSlider = ({
    dataTestId,
    isExpandableTile = false,
    lazyLoad,
    onProductTileClick,
    productItems,
    showSkeletons = false,
    showTitle = true,
    title,
    trackingCategory,
    trackingListName,
    url,
    urlTitle,
    virtualSlide,
    wishlist,
}: {
    readonly productItems: Product[];
    readonly title?: string;
    readonly urlTitle?: string;
    readonly url?: string;
    readonly trackingCategory: TrackingCategory;
    readonly trackingListName?: TrackingListName;
    readonly dataTestId?: string;
    readonly lazyLoad: boolean;
    readonly virtualSlide?: boolean;
    readonly showSkeletons?: boolean;
    readonly showTitle?: boolean;
    readonly isExpandableTile?: boolean;
    readonly wishlist?: string[];
    readonly onProductTileClick?: ProductTileWrapperComponent['onProductTileClick'];
}): JSX.Element | null => {
    const intl = useIntl();
    const highestMatchingBreakpoint = useHighestMatchingBreakpoint();
    const sliderConfig = productSliderConfig[highestMatchingBreakpoint];

    if (!sliderConfig) {
        return null;
    }

    const slides = productItems.map((product, index) => ({
        key: product.productId,
        content: (
            <div className="w-100 p-0-5x">
                <ProductTileWrapper
                    key={product.productId}
                    product={product}
                    position={index + 1}
                    trackingListName={trackingListName}
                    isPortraitTile
                    isExpandableTile={isExpandableTile}
                    lazyLoad={lazyLoad}
                    wishlist={wishlist}
                    onProductTileClick={onProductTileClick}
                />
            </div>
        ),
    }));

    const skeletonSlides = Array.from({ length: sliderConfig.slidesPerView + 1 }, (_, index) => ({
        key: index.toString(),
        content: <SkeletonTile />,
    }));

    return (
        <div data-testid={dataTestId}>
            <div className="d-flex justify-content-between flex-wrap align-items-baseline w-100 mb-3x">
                {showTitle ? <h2 className={styles.headline}>{title}</h2> : null}
                {urlTitle ? (
                    <Link
                        className={classNames(
                            styles.showMore,
                            'd-inline-flex align-content-center',
                        )}
                        variant={LinkVariant.Brand}
                        href={url}
                        internal
                    >
                        <span>{urlTitle ?? intl.formatMessage(messages.showAll)}</span>
                        <ChevronIcon size={21} />
                    </Link>
                ) : null}
            </div>
            <Slider
                a11yNextSlide={intl.formatMessage(messagesSlider.a11yNextSlide)}
                a11yPreviousSlide={intl.formatMessage(messagesSlider.a11yPreviousSlide)}
                key={virtualSlide ? 'virtual' : 'non-virtual'}
                slides={showSkeletons ? skeletonSlides : slides}
                config={sliderConfig}
                classes={{
                    controlsContainer: 'p-0-5x',
                    controlsWrapper: styles.controlsWrapper,
                    swiperSlide: styles.swiperSlide,
                }}
                handleShowNext={() => trackSlide(SlidingDirections.Next, trackingCategory)}
                handleShowPrevious={() => trackSlide(SlidingDirections.Previous, trackingCategory)}
                handleTouchStart={() => trackSlide('', trackingCategory)}
                handleTouchMove={() => trackSlide('', trackingCategory)}
                virtualSlide={virtualSlide}
            />
        </div>
    );
};

export { ProductSlider };
